import type { CmpConfig, CmpPurpose, CmpVendor } from './types.js';

const STYLE_TAG_ID = 'mpwa-cmp-styles';

const STYLE = `
.mpwa-cmp-root{position:fixed;inset:auto 0 0 0;z-index:2147483646;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;}
.mpwa-cmp-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:2147483645;}
.mpwa-cmp-card{max-width:760px;margin:0 auto 16px;background:#fff;color:#0a0a0a;padding:20px 24px;border-radius:16px;box-shadow:0 18px 48px rgba(0,0,0,.18);}
.mpwa-cmp-card.dark{background:#0a0a0a;color:#f5f5f5;}
.mpwa-cmp-title{font-size:18px;font-weight:600;margin:0 0 6px;}
.mpwa-cmp-body{font-size:14px;line-height:1.55;margin:0 0 14px;opacity:.85;}
.mpwa-cmp-row{display:flex;flex-wrap:wrap;gap:8px;justify-content:flex-end;}
.mpwa-cmp-btn{appearance:none;border:0;border-radius:8px;padding:9px 14px;font-size:14px;font-weight:500;cursor:pointer;}
.mpwa-cmp-btn-primary{background:#0a0a0a;color:#fff;}
.mpwa-cmp-card.dark .mpwa-cmp-btn-primary{background:#f5f5f5;color:#0a0a0a;}
.mpwa-cmp-btn-secondary{background:transparent;color:inherit;border:1px solid rgba(0,0,0,.18);}
.mpwa-cmp-card.dark .mpwa-cmp-btn-secondary{border-color:rgba(255,255,255,.2);}
.mpwa-cmp-btn-link{background:transparent;color:inherit;text-decoration:underline;}
.mpwa-cmp-details{margin-top:12px;border-top:1px solid rgba(0,0,0,.08);padding-top:12px;display:none;max-height:50vh;overflow:auto;}
.mpwa-cmp-card.dark .mpwa-cmp-details{border-color:rgba(255,255,255,.1);}
.mpwa-cmp-root.expanded .mpwa-cmp-details{display:block;}
.mpwa-cmp-purpose{display:flex;align-items:flex-start;gap:10px;padding:8px 0;}
.mpwa-cmp-purpose label{flex:1;}
.mpwa-cmp-purpose .name{font-weight:500;font-size:13px;}
.mpwa-cmp-purpose .desc{font-size:12px;opacity:.75;}
.mpwa-cmp-vendors{margin-top:12px;font-size:12px;opacity:.75;}
.mpwa-cmp-root[dir=rtl] .mpwa-cmp-row{justify-content:flex-start;}
`;

function ensureStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const tag = document.createElement('style');
  tag.id = STYLE_TAG_ID;
  tag.textContent = STYLE;
  document.head.appendChild(tag);
}

export interface BannerCallbacks {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onCustom: (purposes: number[]) => void;
}

export type BannerLocale = 'en' | 'ar' | 'pt-BR' | 'zh-CN';

/**
 * Per-locale button labels + helper strings. The title + body strings come
 * from the publisher's CMP config (so the operator can edit them per site);
 * everything else lives here because it's framework-mandated wording — TCF
 * v2.2 + LGPD + PIPL all require the user be told an option exists.
 */
const STR: Record<BannerLocale, {
  customise: string; rejectAll: string; acceptAll: string;
  save: string; partners: string; required: string;
}> = {
  en: { customise: 'Customise', rejectAll: 'Reject all', acceptAll: 'Accept all', save: 'Save preferences', partners: 'Partners: ', required: '(required)' },
  ar: { customise: 'تخصيص', rejectAll: 'رفض الكل', acceptAll: 'موافق', save: 'حفظ التفضيلات', partners: 'الشركاء: ', required: '(ضروري)' },
  'pt-BR': { customise: 'Personalizar', rejectAll: 'Rejeitar tudo', acceptAll: 'Aceitar tudo', save: 'Salvar preferências', partners: 'Parceiros: ', required: '(obrigatório)' },
  'zh-CN': { customise: '自定义', rejectAll: '全部拒绝', acceptAll: '全部接受', save: '保存偏好', partners: '合作伙伴：', required: '(必需)' },
};

function pickTitle(cfg: CmpConfig, locale: BannerLocale): string {
  if (locale === 'ar') return cfg.copy.titleAr;
  if (locale === 'pt-BR' && cfg.copy.titleBr) return cfg.copy.titleBr;
  if (locale === 'zh-CN' && cfg.copy.titleCn) return cfg.copy.titleCn;
  return cfg.copy.titleEn;
}

function pickBody(cfg: CmpConfig, locale: BannerLocale): string {
  if (locale === 'ar') return cfg.copy.bodyAr;
  if (locale === 'pt-BR' && cfg.copy.bodyBr) return cfg.copy.bodyBr;
  if (locale === 'zh-CN' && cfg.copy.bodyCn) return cfg.copy.bodyCn;
  return cfg.copy.bodyEn;
}

export function renderBanner(cfg: CmpConfig, locale: BannerLocale, cbs: BannerCallbacks): () => void {
  ensureStyle();
  const root = document.createElement('div');
  root.className = 'mpwa-cmp-root';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'false');
  root.setAttribute('aria-label', 'Consent management');
  root.dir = locale === 'ar' ? 'rtl' : 'ltr';
  root.lang = locale;

  const backdrop = document.createElement('div');
  backdrop.className = 'mpwa-cmp-backdrop';

  const card = document.createElement('div');
  const dark = cfg.theme === 'dark' || (cfg.theme === 'auto' && matchMedia?.('(prefers-color-scheme: dark)').matches);
  card.className = 'mpwa-cmp-card' + (dark ? ' dark' : '');

  const t = STR[locale];

  const title = document.createElement('h2');
  title.className = 'mpwa-cmp-title';
  title.textContent = pickTitle(cfg, locale);

  const body = document.createElement('p');
  body.className = 'mpwa-cmp-body';
  body.textContent = pickBody(cfg, locale);

  // Details panel (lazy-expanded on "Customise").
  const details = document.createElement('div');
  details.className = 'mpwa-cmp-details';
  const purposeInputs = new Map<number, HTMLInputElement>();
  for (const p of cfg.purposes) {
    details.appendChild(renderPurposeRow(p, locale, t.required, purposeInputs));
  }
  if (cfg.vendors.length) {
    const vendorsLine = document.createElement('div');
    vendorsLine.className = 'mpwa-cmp-vendors';
    vendorsLine.textContent = t.partners + cfg.vendors.map((v) => v.name).join(', ');
    details.appendChild(vendorsLine);
  }

  // Footer row.
  const row = document.createElement('div');
  row.className = 'mpwa-cmp-row';

  const customiseBtn = button('mpwa-cmp-btn mpwa-cmp-btn-link', t.customise);
  customiseBtn.onclick = () => root.classList.toggle('expanded');

  const rejectBtn = button('mpwa-cmp-btn mpwa-cmp-btn-secondary', t.rejectAll);
  rejectBtn.onclick = () => cbs.onRejectAll();

  const acceptBtn = button('mpwa-cmp-btn mpwa-cmp-btn-primary', t.acceptAll);
  acceptBtn.onclick = () => cbs.onAcceptAll();

  const saveBtn = button('mpwa-cmp-btn mpwa-cmp-btn-primary', t.save);
  saveBtn.onclick = () => {
    const granted: number[] = [];
    for (const [id, input] of purposeInputs) if (input.checked) granted.push(id);
    cbs.onCustom(granted);
  };

  row.append(customiseBtn, rejectBtn, acceptBtn, saveBtn);

  card.append(title, body, details, row);
  document.body.appendChild(backdrop);
  document.body.appendChild(root);
  root.appendChild(card);

  return () => {
    root.remove();
    backdrop.remove();
  };
}

function button(cls: string, label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = label;
  return b;
}

function renderPurposeRow(
  p: CmpPurpose,
  locale: BannerLocale,
  requiredTag: string,
  bag: Map<number, HTMLInputElement>,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'mpwa-cmp-purpose';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.checked = p.required;
  input.disabled = p.required;
  input.id = `mpwa-cmp-p-${p.id}`;
  bag.set(p.id, input);

  const label = document.createElement('label');
  label.htmlFor = input.id;
  const name = document.createElement('div');
  name.className = 'name';
  name.textContent = `${p.id}. ${p.name}`;
  const desc = document.createElement('div');
  desc.className = 'desc';
  desc.textContent = p.description;
  label.append(name, desc);

  if (p.required) {
    const tag = document.createElement('span');
    tag.style.fontSize = '11px';
    tag.style.opacity = '.6';
    tag.style.marginInlineStart = '6px';
    tag.textContent = ' ' + requiredTag;
    name.appendChild(tag);
  }
  // locale param accepted for future per-locale tweaks (date format, etc.)
  void locale;

  row.append(input, label);
  return row;
}

export function flattenVendorIds(vendors: CmpVendor[]): number[] {
  return vendors.map((v) => v.id);
}
